// A styled hover / focus / tap popover, in place of native `title` tooltips (GRO-276).
//
// It opens after a short delay on hover, at once on keyboard focus, and toggles on Enter, Space
// or a tap. It stays open while the pointer moves from the trigger into the card. The card
// renders into a portal on <body> with fixed positioning, so the table's overflow never clips
// it; it flips above the trigger when there is no room below and is clamped inside the viewport.
// Its content is a render function, mounted only while the card is open.

import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type {
  KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent, ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { LinkedText } from './LinkedText';
import { TRIGGER_KEYS, nextHoverCardOpen } from '../lib/hoverCardState';
import type { HoverCardEvent } from '../lib/hoverCardState';

const OPEN_DELAY_MS = 150;
const CLOSE_DELAY_MS = 120;
const GAP_PX = 6;
const EDGE_PX = 8;
const FOLLOW_INTERVAL_MS = 120;

interface Position {
  top: number;
  left: number;
}

// Below the trigger when it fits, else above, else beside it: the card never covers the trigger it
// explains, and never goes past the viewport's edges. Beside is the last resort (a tall card in a
// short window); it takes the side with more room.
function placeCard(trigger: DOMRect, card: DOMRect): Position {
  const maxLeft = window.innerWidth - card.width - EDGE_PX;
  const maxTop = window.innerHeight - card.height - EDGE_PX;
  const fitsBelow = trigger.bottom + GAP_PX + card.height <= window.innerHeight - EDGE_PX;
  if (fitsBelow) return { top: trigger.bottom + GAP_PX, left: Math.max(EDGE_PX, Math.min(trigger.left, maxLeft)) };
  const fitsAbove = trigger.top - GAP_PX - card.height >= EDGE_PX;
  if (fitsAbove) return { top: trigger.top - GAP_PX - card.height, left: Math.max(EDGE_PX, Math.min(trigger.left, maxLeft)) };

  // Beside: left of the trigger when there is more room there, else right.
  const roomLeft = trigger.left - GAP_PX - EDGE_PX;
  const roomRight = window.innerWidth - trigger.right - GAP_PX - EDGE_PX;
  const top = Math.max(EDGE_PX, Math.min(trigger.top, maxTop));
  if (roomLeft >= card.width || roomLeft >= roomRight) {
    return { top, left: Math.max(EDGE_PX, trigger.left - GAP_PX - card.width) };
  }
  return { top, left: Math.min(trigger.right + GAP_PX, Math.max(EDGE_PX, maxLeft)) };
}

export function HoverCard({
  card,
  children,
  className,
  forceOpen = false,
  label,
}: {
  // Called only while the card is open.
  card: () => ReactNode;
  children: ReactNode;
  className?: string;
  // Dev only: keep this one open (for screenshots), via `?hover=<account id>:<column>`.
  forceOpen?: boolean;
  // What the card is about, for screen readers.
  label: string;
}) {
  const [open, setOpen] = useState(forceOpen);
  const [position, setPosition] = useState<Position | null>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<number | null>(null);
  const lastPointerRef = useRef<string>('mouse');
  const cardId = useId();

  // Every open / close goes through the one pure rule (lib/hoverCardState.ts).
  const apply = useCallback((event: HoverCardEvent) => {
    setOpen(current => nextHoverCardOpen(current, event, forceOpen));
  }, [forceOpen]);

  const clearTimer = useCallback(() => {
    if (timerRef.current != null) window.clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  function later(event: HoverCardEvent, delay: number) {
    clearTimer();
    timerRef.current = window.setTimeout(() => apply(event), delay);
  }

  // Mouse hover opens after the delay; touch is handled by the tap toggle instead.
  function onPointerEnter(event: ReactPointerEvent) {
    lastPointerRef.current = event.pointerType;
    if (event.pointerType === 'mouse') later('hover_settled', OPEN_DELAY_MS);
  }
  function onPointerLeave(event: ReactPointerEvent) {
    if (event.pointerType === 'mouse') later('pointer_left', CLOSE_DELAY_MS);
  }

  // Enter / Space toggle the card; those keys and j / k stay off the row underneath.
  function onKeyDown(event: ReactKeyboardEvent) {
    if (event.key === 'Escape' && open) {
      event.stopPropagation();
      apply('escape');
      return;
    }
    if (!TRIGGER_KEYS.has(event.key)) return;

    // A link inside the trigger (the Account cell's name and chips) keeps its own Enter.
    if ((event.target as HTMLElement).closest('a')) return;
    event.stopPropagation();
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      apply('activate');
    }
  }

  useEffect(() => () => clearTimer(), [clearTimer]);
  useEffect(() => { if (forceOpen) setOpen(true); }, [forceOpen]);

  // Follow the trigger while open: on scroll and resize at once, and on a short timer for the
  // page settling around it (the brief and funding load after the table and move the row). A
  // timer rather than requestAnimationFrame, which stops in a background tab.
  useLayoutEffect(() => {
    if (!open) return;
    let last = '';
    function update() {
      const trigger = triggerRef.current?.getBoundingClientRect();
      const box = cardRef.current?.getBoundingClientRect();
      if (!trigger || !box) return;
      const next = placeCard(trigger, box);
      const key = `${Math.round(next.top)}:${Math.round(next.left)}`;
      if (key === last) return;
      last = key;
      setPosition(next);
    }
    update();
    const timer = window.setInterval(update, FOLLOW_INTERVAL_MS);
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open]);

  // A tap elsewhere, or Escape, closes a card opened by tap or focus.
  useEffect(() => {
    if (!open || forceOpen) return;
    function onDown(event: PointerEvent) {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || cardRef.current?.contains(target)) return;
      apply('outside_press');
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') apply('escape');
    }
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, forceOpen, apply]);

  return (
    <>
      <span
        ref={triggerRef}
        className={'hovertrigger' + (className ? ' ' + className : '')}
        role="button"
        tabIndex={0}
        aria-label={label}
        aria-expanded={open}
        aria-controls={open ? cardId : undefined}
        onPointerEnter={onPointerEnter}
        onPointerLeave={onPointerLeave}
        onKeyDown={onKeyDown}
        onFocus={() => { clearTimer(); apply('focus'); }}
        onBlur={() => later('blur', CLOSE_DELAY_MS)}
        onPointerUp={event => {
          // Tap toggles on touch.
          lastPointerRef.current = event.pointerType;
          if (event.pointerType === 'mouse') return;
          event.stopPropagation();
          apply('activate');
        }}
        // A tap must not also open the account panel underneath; a mouse click still does.
        onClick={event => { if (lastPointerRef.current !== 'mouse') event.stopPropagation(); }}
      >
        {children}
      </span>
      {open ? createPortal(
        <div
          ref={cardRef}
          id={cardId}
          role="tooltip"
          className="hovercard"
          data-hovercard-open=""
          style={position ? { top: position.top, left: position.left } : { top: -9999, left: -9999 }}
          onPointerEnter={clearTimer}
          onPointerLeave={onPointerLeave}
          onClick={event => event.stopPropagation()}
        >
          {card()}
        </div>,
        document.body,
      ) : null}
    </>
  );
}

// The card's body: an optional heading, then label / value rows.
export function CardTitle({ children }: { children: ReactNode }) {
  return <div className="hctitle">{children}</div>;
}

// Values stay on one line; only a sentence-length value (a reason) may wrap.
const PROSE_CHARACTERS = 52;

export function CardRows({ rows }: { rows: Array<[string, ReactNode]> }) {
  return (
    <dl className="hcrows">
      {rows.map(([label, value], index) => (
        <div key={index} className="hcrow">
          <dt>{label}</dt>
          {/* A string's http(s) URLs become links: evidence carries its source inline. */}
          <dd className={typeof value === 'string' && value.length > PROSE_CHARACTERS ? 'prose' : undefined}>
            {typeof value === 'string' ? <LinkedText text={value} /> : value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export function CardSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="hcsection">
      <div className="hcsectiontitle">{title}</div>
      {children}
    </div>
  );
}
